import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../ui/button';

interface DrilldownPagerProps {
  page: number;
  totalPages: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  /** id prefix for the "Go to page" input (unique per modal). */
  idPrefix?: string;
}

/** 1 … 4 5 6 … 20 — the page list the Scheduled Dashboard drill-downs use. */
export function buildPageList(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: Array<number | '…'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push('…');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

/**
 * Numbered pager + "Go to page" jump — the same control the Scheduled
 * Dashboard drill-down modals render, so both dashboards page the same way.
 */
export function DrilldownPager({ page, totalPages, loading = false, onPageChange, idPrefix = 'pld' }: DrilldownPagerProps) {
  const [pageInput, setPageInput] = useState(String(page));
  useEffect(() => { setPageInput(String(page)); }, [page]);
  const pageButtons = useMemo(() => buildPageList(page, totalPages), [page, totalPages]);

  const goTo = (p: number) => {
    if (p < 1 || p > totalPages || p === page || loading) return;
    onPageChange(p);
  };
  const commitInput = () => {
    const n = parseInt(pageInput, 10);
    if (!Number.isNaN(n)) goTo(Math.max(1, Math.min(totalPages, n)));
    else setPageInput(String(page));
  };

  return (
    <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => goTo(page - 1)} aria-label="Previous page">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {pageButtons.map((p, idx) => (
          p === '…' ? (
            <span key={`gap-${idx}`} className="px-2 text-xs text-muted-foreground">…</span>
          ) : (
            <Button
              key={p} size="sm"
              variant={p === page ? 'default' : 'outline'}
              disabled={loading}
              onClick={() => goTo(p)}
              aria-current={p === page ? 'page' : undefined}
              className="min-w-[2.25rem]"
            >
              {p}
            </Button>
          )
        ))}
        <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => goTo(page + 1)} aria-label="Next page">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <label htmlFor={`${idPrefix}-page-jump`}>Go to page</label>
        <input
          id={`${idPrefix}-page-jump`}
          type="number" min={1} max={totalPages}
          value={pageInput} disabled={loading}
          onChange={(e) => setPageInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commitInput(); }}
          onBlur={commitInput}
          className="h-7 w-16 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
        />
        <span>of {totalPages.toLocaleString()}</span>
      </div>
    </div>
  );
}
